import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./i18n";
import Login from "./pages/Login";
import FarmerDashboard from "./pages/FarmerDashboard";
import OfficialDashboard from "./pages/OfficialDashboard";
import ApplicationDetail from "./pages/ApplicationDetail";
import NewApplication from "./pages/NewApplication";
import DocumentationPage from "./pages/DocumentationPage";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import ForcePasswordChangeModal from "./components/ForcePasswordChangeModal";
import BottomNavBar from "./components/BottomNavBar";
import InstallPrompt from "./components/InstallPrompt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 15000, // 15 second timeout
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercept 403 demo-user errors and emit a global event
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || "";
      if (detail.includes("Demo account")) {
        window.dispatchEvent(new CustomEvent("demo-write-blocked", { detail }));
      }
    }
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false - no loading spinner
  const [showForcePasswordModal, setShowForcePasswordModal] = useState(false);
  const [demoBlockMsg, setDemoBlockMsg] = useState("");

  // Listen for demo write-blocked events
  useEffect(() => {
    const handler = (e) => setDemoBlockMsg(e.detail || "Demo account: Read-only access.");
    window.addEventListener("demo-write-blocked", handler);
    return () => window.removeEventListener("demo-write-blocked", handler);
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const res = await api.get("/auth/me", { timeout: 10000 });
          setUser(res.data);
          localStorage.setItem("userName", res.data.name || "");
          // Check if password change is required
          const mustChangePassword = localStorage.getItem("mustChangePassword");
          if (mustChangePassword === "true" && res.data.role === "official") {
            setShowForcePasswordModal(true);
          }
        } catch (error) {
          console.log("Auth check failed:", error.message);
          localStorage.removeItem("token");
          localStorage.removeItem("mustChangePassword");
          setUser(null);
        }
      }
    };
    
    loadUser();
  }, []);

  // Check for password change requirement when user logs in
  useEffect(() => {
    if (user && user.role === "official") {
      const mustChangePassword = localStorage.getItem("mustChangePassword");
      if (mustChangePassword === "true") {
        setShowForcePasswordModal(true);
      }
    }
  }, [user]);

  const handlePasswordChanged = () => {
    // Clear the flag and close modal
    localStorage.removeItem("mustChangePassword");
    setShowForcePasswordModal(false);
  };

  // Remove loading spinner completely - just render the app
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route
            path="/farmer/dashboard"
            element={
              <ProtectedRoute>
                <FarmerDashboard user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/official/dashboard"
            element={
              <ProtectedRoute>
                <OfficialDashboard user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/application/:id"
            element={
              <ProtectedRoute>
                <ApplicationDetail user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/farmer/new-application"
            element={
              <ProtectedRoute>
                <NewApplication user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={<DocumentationPage />}
          />
          <Route
            path="/"
            element={
              user ? (
                <Navigate
                  to={
                    user.role === "farmer"
                      ? "/farmer/dashboard"
                      : "/official/dashboard"
                  }
                />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
        
        {/* Mobile Bottom Navigation - only show when logged in */}
        {user && <BottomNavBar role={user.role} />}
        
        {/* PWA Install Prompt */}
        <InstallPrompt />
      </BrowserRouter>
      
      {/* Force Password Change Modal - blocks UI for officials who need to change password */}
      <ForcePasswordChangeModal
        isOpen={showForcePasswordModal}
        onPasswordChanged={handlePasswordChanged}
        userName={user?.name || ""}
      />
      
      <Toaster position="top-center" richColors closeButton />

      {/* Demo User Write-Blocked Dialog */}
      <Dialog open={!!demoBlockMsg} onOpenChange={(open) => { if (!open) setDemoBlockMsg(""); }}>
        <DialogContent className="sm:max-w-md" data-testid="demo-block-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-amber-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Demo Account
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-sm text-slate-600 py-3">
            {demoBlockMsg || "This is a read-only demo account. Write operations are not permitted."}
          </p>
          <DialogFooter className="justify-center sm:justify-center">
            <Button onClick={() => setDemoBlockMsg("")} className="bg-amber-600 hover:bg-amber-700" data-testid="demo-block-close">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}

export default App;